import { GridRowModes, GridToolbarContainer, GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid-premium';
import { Field, FieldArray, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';

import { dataGridPremiumColumns } from '../../utils/helper';

import {
  DataGridIdentifiers,
  FormErrorUI,
  AutoCompleteSelectUI,
  CheckboxUI,
  AddButtonUi,
  DatagridPremiumUi,
  InputUI,
  ColorPickerUI,
  ChromeColorPickerUI,
  ColorCircleUi,
  OrderArrowsUI,
} from '../Interface';
import { ColorPicker } from '../../config/ColorPicker';
import { useState } from 'react';
import { Popover, Box } from '@mui/material';

function EditToolbar(props) {
  const { disabled, push, defaultProjectTypeStatusItems } = props;
  const { values, validateField } = useFormikContext();

  const handleClick = async () => {
    const newVisOrder = (values.projectTypeStatusItems?.length || 0) + 1;

    const newData = {
      ...defaultProjectTypeStatusItems,
      id: uuidv4(),
      visOrder: newVisOrder,
    };

    await push(newData);

    const fieldsToValidate = [
      `projectTypeStatusItems.${values.projectTypeStatusItems.length ?? 0}.name`,
      `projectTypeStatusItems.${values.projectTypeStatusItems.length ?? 0}.color`,
      `projectTypeStatusItems.${values.projectTypeStatusItems.length ?? 0}.onKanban`,
      `projectTypeStatusItems.${values.projectTypeStatusItems.length ?? 0}.preparedness`,
    ];

    for (const field of fieldsToValidate) {
      await validateField(field);
    }
  };

  return (
    <GridToolbarContainer>
      <AddButtonUi disabled={disabled} onClick={handleClick} />
    </GridToolbarContainer>
  );
}

const ProjectTypeStatusItems = ({ disabled, defaultProjectTypeStatusItems }) => {
  const apiRef = useGridApiRef();
  const { values, validateField, setFieldValue, errors } = useFormikContext();
  const [anchorEl, setAnchorEl] = useState(null);

  const getValueIndex = (values, id) => {
    return values.projectTypeStatusItems.findIndex((rc) => rc.id === id);
  };

  const handlePopoverOpen = (event) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'color-popover' : undefined;

  const handleOrderChange = (id, currentOrder, direction) => {
    const newOrder = direction === 'up' ? currentOrder - 1 : currentOrder + 1;

    if (newOrder < 1 || newOrder > values.projectTypeStatusItems.length) {
      return;
    }

    const updatedList = [...values.projectTypeStatusItems]
      .map((item) => {
        if (item.id === id) {
          return { ...item, visOrder: newOrder };
        } else if (item.visOrder === newOrder) {
          return { ...item, visOrder: currentOrder };
        }
        return item;
      })
      .sort((a, b) => a.visOrder - b.visOrder);

    setFieldValue('projectTypeStatusItems', updatedList);
  };

  const columns = (setFieldValue, validateField, errors, remove) => {
    return dataGridPremiumColumns([
      {
        field: 'defaultStatusId',
        type: 'boolean',
        headerName: 'Alapértelmezett státusz',
        editable: true,
        renderCell: (params) => {
          const isChecked = values.defaultStatusId === params.id;
          return <CheckboxUI checked={isChecked} disabled />;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return (
            <Field
              name="defaultStatusId"
              component={CheckboxUI}
              helperText={<FormErrorUI message={values?.defaultStatusId} />}
              checked={values.defaultStatusId === values.projectTypeStatusItems[valueIndex]?.id}
              variant="standard"
              onChange={(e) => {
                setFieldValue('defaultStatusId', e.target.checked ? data.id : null);
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.checked,
                });
              }}
              onBlur={async () => await validateField('defaultStatusId')}
            />
          );
        },
      },
      {
        field: 'name',
        headerName: 'Megnevezés',
        type: 'string',
        editable: true,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`projectTypeStatusItems.${valueIndex}.name`}
              type="text"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.projectTypeStatusItems?.[valueIndex]?.name} />}
              onChange={(e) => {
                setFieldValue(`projectTypeStatusItems.${valueIndex}.name`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`projectTypeStatusItems.${valueIndex}.name`)}
            />
          );
        },
      },
      {
        field: 'color',
        headerName: 'Szín',
        type: 'string',
        editable: true,
        renderCell: (params) => {
          return <ColorCircleUi color={params.value ?? '#FFFFFF'} size={20} />;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <div className="ml-1 w-[100%] flex justify-center">
              <div
                className={`${!disabled ? 'cursor-pointer' : 'cursor-not-allowed'} rounded-full shadow-[rgba(0,_0,_0,_0.54)_0px_1px_8px] w-[20px] h-[20px] mt-2.5`}
                style={{ background: values.projectTypeStatusItems[valueIndex].color }}
                onClick={handlePopoverOpen}
              />
              <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handlePopoverClose}
                anchorOrigin={{
                  vertical: 'center',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'center',
                  horizontal: 'left',
                }}
              >
                <ChromeColorPickerUI
                  color={values.projectTypeStatusItems[valueIndex].color}
                  onChange={(color) => {
                    setFieldValue(`projectTypeStatusItems.${valueIndex}.color`, color.hex);
                    data.api.setEditCellValue({
                      id: data.id,
                      field: data.field,
                      value: color.hex,
                    });
                  }}
                />
              </Popover>
            </div>
          );
        },
      },
      {
        field: 'preparedness',
        headerName: 'Készültség',
        type: 'string',
        editable: true,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`projectTypeStatusItems.${valueIndex}.preparedness`}
              type="number"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.projectTypeStatusItems?.[valueIndex]?.preparedness} />}
              onChange={(e) => {
                setFieldValue(`projectTypeStatusItems.${valueIndex}.preparedness`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`projectTypeStatusItems.${valueIndex}.preparedness`)}
            />
          );
        },
      },
      {
        field: 'onKanban',
        type: 'boolean',
        headerName: 'Kanbanon megjelenik',
        editable: true,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return (
            <Field
              name={`projectTypeStatusItems.${valueIndex}.onKanban`}
              component={CheckboxUI}
              helperText={<FormErrorUI message={errors?.projectTypeStatusItems?.[valueIndex]?.onKanban} />}
              checked={data.row?.onKanban === true}
              variant="standard"
              onChange={(e) => {
                setFieldValue(`projectTypeStatusItems.${valueIndex}.onKanban`, e.target.checked ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.checked,
                });
              }}
              onBlur={async () => await validateField(`projectTypeStatusItems.${valueIndex}.onKanban`)}
            />
          );
        },
      },
      {
        field: 'visOrder',
        headerName: 'Sorrend',
        renderCell: (params) => {
          const disabledOrder = params?.api?.getSortModel()[0]?.field !== 'visOrder';

          const handleUpClick = (e) => {
            e.stopPropagation();
            handleOrderChange(params.row.id, params.row.visOrder, 'up');
          };
          const handleDownClick = (e) => {
            e.stopPropagation();
            handleOrderChange(params.row.id, params.row.visOrder, 'down');
          };
          return (
            <Box display="flex" alignItems="center">
              <Box mr={1}>{params.row.visOrder}</Box>
              <OrderArrowsUI
                onUpClick={handleUpClick}
                onDownClick={handleDownClick}
                disabled={disabled || disabledOrder}
              />
            </Box>
          );
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Szerk.',
        cellClassName: 'actions',
        sortable: false,
        getActions: ({ id }) => {
          return [
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={() => {
                const valueIndex = getValueIndex(values, id);

                if (values.defaultStatusId === id) {
                  setFieldValue('defaultStatusId', null);
                }

                return remove(valueIndex);
              }}
              color="inherit"
              disabled={disabled}
            />,
          ];
        },
      },
    ]);
  };

  return (
    <div className="overflow-x-auto">
      <div className="align-middle">
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
          <FieldArray name="projectTypeStatusItems">
            {({ remove, push }) => (
              <DatagridPremiumUi
                apiRef={apiRef}
                datagridIdentifier={DataGridIdentifiers.ProjectTypeStatusItems}
                height={400}
                columns={columns(setFieldValue, validateField, errors, remove)}
                rows={values?.projectTypeStatusItems ?? []}
                slots={{
                  toolbar: EditToolbar,
                }}
                headerFilters={false}
                slotProps={{
                  toolbar: {
                    disabled,
                    push,
                    defaultProjectTypeStatusItems,
                  },
                }}
                onCellDoubleClick={(_params, event) => {
                  event.defaultMuiPrevented = true;
                }}
                editMode="row"
                getRowHeight={() => 'auto'}
                onCellClick={(params, event) => {
                  if (disabled) return;
                  const rowMode = apiRef.current.getRowMode(params.row.id);
                  if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
                }}
              />
            )}
          </FieldArray>
        </div>
      </div>
    </div>
  );
};

export default ProjectTypeStatusItems;
